
import { ElForm } from 'element-ui/types/form'
import { Vue, Component } from 'vue-property-decorator'
import { AiFeatureService } from '@/services/index'
import {
    IAiFeature,
    IUpsertFeatureParams,
    IFeatureKey,
    IAvailableProvider,
    IParamDef
} from '@/types/ai-types'

type DialogMode = 'create' | 'edit'

@Component({
    name: 'AiFeatureEditDialog'
})
export default class extends Vue {
    private visible = false
    private submitting = false
    private mode: DialogMode = 'create'

    private featureKeys: IFeatureKey[] = []
    private availableProviders: IAvailableProvider[] = []
    private schemaParams: IParamDef[] = []
    private loadingSchema = false
    private providerPlaceholder = '请选择模型'

    /** 动态参数表单值：顶层键对应 schema key；
     *  group 类型时该槽是对象，子键对应 children.key */
    private formConfig: Record<string, any> = {}

    private form: IUpsertFeatureParams = this.emptyForm()

    private rules: any = {
        feature_key: [
            { required: true, message: '请选择功能标识', trigger: 'change' }
        ],
        feature_name: [
            { required: true, message: '请输入功能名称', trigger: 'blur' },
            { max: 64, message: '名称不超过 64 个字符', trigger: 'blur' }
        ],
        provider_id: [
            { required: true, message: '请选择绑定模型', trigger: 'change' }
        ]
    }

    /* ===========================================================
     *                          对外方法
     * =========================================================== */

    /**
     * 打开弹窗。
     *   - 先重置所有 UI 状态
     *   - 并行加载下拉数据
     *   - 编辑模式：先填 form + 解析已有 model_config，await schema 后再开 visible
     *     （保证弹窗打开时 schema 参数已就绪、formConfig 已 sanitize+补默认值）
     */
    async show(mode: DialogMode, row?: IAiFeature) {
        this.mode = mode
        this.submitting = false
        this.schemaParams = []
        this.formConfig = {}
        this.providerPlaceholder = '请选择模型'

        await Promise.all([
            this.loadFeatureKeys(),
            this.loadAvailableProviders()
        ])

        if (mode === 'edit' && row) {
            this.form = {
                id: row.id,
                feature_key: row.feature_key,
                feature_name: row.feature_name,
                provider_id: row.provider_id,
                prompt_template: row.prompt_template || '',
                model_config: row.model_config || '',
                description: row.description || '',
                status: row.status
            }

            // 当前绑定的 provider 是否仍在可用列表里
            const providerAvailable = this.availableProviders.some(p => p.id === row.provider_id)
            if (!providerAvailable) {
                this.providerPlaceholder = '当前模型不可用，请重新选择'
            }

            // 等 schema 加载完成 → 再用 schema 蓝图构建 formConfig（一步到位，不给 null 留通路）
            if (row.provider_id && providerAvailable) {
                await this.loadConfigSchema(row.provider_id)
                this.buildFormConfig(row.model_config)
            }
        } else {
            this.form = this.emptyForm()
        }

        this.visible = true
        this.$nextTick(() => {
            const fr = this.$refs.featureForm as ElForm
            fr && fr.clearValidate()
        })
    }

    hide() {
        this.submitting = false
        this.visible = false
    }

    /* ===========================================================
     *                        下拉数据加载
     * =========================================================== */

    private async loadFeatureKeys() {
        try {
            const result = await AiFeatureService.listFeatureKeys()
            if (result.status === 200) {
                this.featureKeys = result.data || []
            }
        } catch {
            this.featureKeys = []
        }
    }

    private async loadAvailableProviders() {
        try {
            const result = await AiFeatureService.listAvailableProviders()
            if (result.status === 200) {
                this.availableProviders = result.data || []
            }
        } catch {
            this.availableProviders = []
        }
    }

    private providerOptionLabel(p: IAvailableProvider): string {
        const name = p.name || ''
        const model = p.model || ''
        if (name && model) return `${name}（${model}）`
        if (name) return name
        if (model) return model
        return `模型 #${p.id}`
    }

    /* ===========================================================
     *                       schema / formConfig
     * =========================================================== */

    private async loadConfigSchema(providerId: number) {
        this.loadingSchema = true
        try {
            const result = await AiFeatureService.getConfigSchema(providerId)
            if (result.status === 200 && result.data) {
                this.schemaParams = result.data.params || []
            } else {
                this.schemaParams = []
            }
        } catch {
            this.schemaParams = []
        } finally {
            this.loadingSchema = false
        }
    }

    /**
     * 以 schema 为蓝图，一次性构建 formConfig（所有属性走 $set）。
     * 优先取后端 model_config 中的值（跳过 null），缺失则用 schema 默认值。
     * 保证没有任何键的值是 null，彻底杜绝 el-input-number 对 null 调用 toString()。
     */
    private buildFormConfig(modelConfigJson: string) {
        const stored = this.parseModelConfig(modelConfigJson)
        const cfg: Record<string, any> = {}
        // 必须在赋值给 this.formConfig 之前用 $set 填满，避免 template 在 nextTick 读到不完整的状态
        this.schemaParams.forEach(p => {
            if (p.type === 'group' && p.children && p.children.length > 0) {
                const group: Record<string, any> = {}
                p.children.forEach(c => {
                    const storedGroup = (stored[p.key] && typeof stored[p.key] === 'object') ? stored[p.key] : null
                    const storedVal = storedGroup ? storedGroup[c.key] : undefined
                    group[c.key] = (storedVal !== null && storedVal !== undefined) ? storedVal : this.getDefault(c)
                })
                cfg[p.key] = group
            } else {
                const storedVal = stored[p.key]
                cfg[p.key] = (storedVal !== null && storedVal !== undefined) ? storedVal : this.getDefault(p)
            }
        })
        // 一次性赋给 data 属性，Vue 会对嵌套对象深层 observe
        this.formConfig = cfg
    }

    /** 把 model_config JSON 字符串解析为 plain object（解析失败返回 {}） */
    private parseModelConfig(jsonStr: string): Record<string, any> {
        if (!jsonStr) return {}
        try {
            const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
            return (obj && typeof obj === 'object' && !Array.isArray(obj)) ? obj : {}
        } catch {
            return {}
        }
    }

    /** 取 schema 节点的默认值；schema 没给时按类型兜底，永不返回 null */
    private getDefault(p: IParamDef): any {
        let val: any
        if (p.type === 'select') {
            val = p.default_option !== undefined ? p.default_option : p.default_value
        } else {
            val = p.default_value
        }
        if (val !== null && val !== undefined) return val
        // 兜底
        if (p.type === 'number' || p.type === 'integer') {
            return p.min !== undefined ? p.min : 0
        }
        if (p.type === 'boolean') return false
        if (p.type === 'select' && p.options && p.options.length > 0) {
            return p.options[0].value
        }
        return ''
    }

    /* ===========================================================
     *                          事件处理
     * =========================================================== */

    private onFeatureKeyChange(key: string) {
        const found = this.featureKeys.find(k => k.key === key)
        if (found && !this.form.feature_name) {
            // 仅在名称为空时自动填充，避免覆盖用户编辑
            this.form.feature_name = found.label
        }
    }

    private async onProviderChange(providerId: number) {
        if (!providerId) return
        // 关键顺序：先清 schemaParams 让模板的 v-if 关闭 → 再清 formConfig
        // 避免「schemaParams 有值 但 formConfig 是空」引起 group 子字段访问 undefined
        this.schemaParams = []
        this.formConfig = {}
        await this.loadConfigSchema(providerId)
        this.buildFormConfig('')
    }

    private onCancel() {
        if (this.submitting) return
        this.visible = false
    }

    private onDialogClose(done: Function) {
        if (this.submitting) return
        done()
    }

    private onSubmit() {
        const fr = this.$refs.featureForm as ElForm
        fr.validate((isValid: boolean) => {
            if (!isValid) return
            this.submitting = true
            const params: IUpsertFeatureParams = { ...this.form, model_config: JSON.stringify(this.formConfig || {}) }
            this.$emit(this.mode === 'edit' ? 'update' : 'create', params)
        })
    }

    /* ===========================================================
     *                          工具方法
     * =========================================================== */

    private emptyForm(): IUpsertFeatureParams {
        return {
            feature_key: '',
            feature_name: '',
            provider_id: 0 as any,
            prompt_template: '',
            model_config: '',
            description: '',
            status: 1
        }
    }

    private resolveComponent(param: IParamDef): string {
        switch (param.type) {
            case 'boolean': return 'el-switch'
            case 'select': return 'el-select'
            case 'number': return 'el-slider'
            case 'integer': return 'el-input-number'
            default: return 'el-input'
        }
    }

    private resolveProps(param: IParamDef): any {
        const props: any = {
            placeholder: '请输入' + param.label
        }
        if (param.type === 'number' || param.type === 'integer') {
            // 关键：el-input-number / el-slider 对 min/max/step 任何 null/undefined 都可能崩
            // 必须保证类型为 number 且 step>0
            if (typeof param.min === 'number') props.min = param.min
            if (typeof param.max === 'number') props.max = param.max
            if (typeof param.step === 'number' && param.step > 0) props.step = param.step
            if (param.type === 'integer') {
                props.precision = 0
                // integer 默认值（防止后端 schema 漏给 min/max/step）
                if (props.min === undefined) props.min = 0
                if (props.max === undefined) props.max = Number.MAX_SAFE_INTEGER
                if (props.step === undefined) props.step = 1
            }
        }
        if (param.type === 'select') {
            props.placeholder = '请选择' + param.label
            props.clearable = true
        }
        if (param.type === 'boolean') {
            props['active-value'] = true
            props['inactive-value'] = false
        }
        if (param.type === 'number') {
            // el-slider 必须有 min/max（且 step 必须为正数）
            if (props.min === undefined) props.min = 0
            if (props.max === undefined) props.max = 1
            if (props.step === undefined) props.step = 0.01
            props['show-input'] = true
        }
        return props
    }

    /**
     * group 类型内，第一个子字段视为「总开关」；
     *   关闭时，其余子字段置灰，连动禁用。
     *   （children[0] 默认作为开关，找不到时返回 false 不影响其他场景）
     */
    private isGroupChildDisabled(group: IParamDef, child: IParamDef): boolean {
        if (!group.children || group.children.length === 0) return false
        const head = group.children[0]
        if (head.key === child.key) return false
        const groupObj = this.formConfig[group.key]
        return !!(groupObj && groupObj[head.key] === false)
    }
}
